import { DropDownList } from '@progress/kendo-react-dropdowns'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useLogEvent } from '~/hooks/useLogEvent'

import { type DocumentQaFilterState } from '../utils'

type FilterOption = {
  label: string
  value: string | null
}

type Brand = {
  id: number
  name: string
}

type BrandGroup = {
  brands: Brand[]
  id: number
  name: string
}

type Survey = {
  brandGroups: BrandGroup[]
  id: string
  title: string
}

type DocumentQaFiltersProps = {
  filters: DocumentQaFilterState
  onFiltersChange: (filters: DocumentQaFilterState) => void
  options: {
    surveys: Survey[]
  }
}

export const DocumentQaFilters: React.FC<DocumentQaFiltersProps> = ({
  filters,
  onFiltersChange,
  options,
}) => {
  const { t } = useTranslation()
  const logEvent = useLogEvent()

  const surveyItems: FilterOption[] = React.useMemo(
    () => options.surveys.map((s) => ({ label: s.title, value: s.id })),
    [options.surveys],
  )

  const selectedSurvey = React.useMemo(
    () => options.surveys.find((s) => s.id === filters.surveyId),
    [filters.surveyId, options.surveys],
  )

  const brandGroupItems: FilterOption[] = React.useMemo(
    () =>
      (selectedSurvey?.brandGroups ?? []).map((bg) => ({
        label: bg.name,
        value: String(bg.id),
      })),
    [selectedSurvey],
  )

  const selectedBrandGroup = React.useMemo(
    () =>
      selectedSurvey?.brandGroups.find(
        (bg) => String(bg.id) === filters.brandGroupId,
      ),
    [filters.brandGroupId, selectedSurvey],
  )

  const brandItems: FilterOption[] = React.useMemo(
    () =>
      (selectedBrandGroup?.brands ?? []).map((b) => ({
        label: b.name,
        value: String(b.id),
      })),
    [selectedBrandGroup],
  )

  const placeholderOption: FilterOption = React.useMemo(
    () => ({ label: '---', value: null }),
    [],
  )

  const fieldRowStyle = {
    alignItems: 'center',
    display: 'flex',
    fontSize: '13px',
    gap: '6px',
  } as const

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '16px',
      }}
    >
      <div style={fieldRowStyle}>
        {t('document_qa_filter_survey_label')}
        <DropDownList
          data={surveyItems}
          dataItemKey="value"
          defaultItem={placeholderOption}
          style={{ width: '200px' }}
          textField="label"
          value={surveyItems.find((i) => i.value === filters.surveyId) ?? null}
          onChange={(e) => {
            const surveyId = (e.value as FilterOption)?.value ?? null
            onFiltersChange({
              brandGroupId: null,
              brandId: null,
              surveyId,
            })
            if (surveyId) {
              logEvent('Select survey')
            }
          }}
        />
      </div>

      <div style={fieldRowStyle}>
        {t('document_qa_filter_brand_group_label')}
        <DropDownList
          data={brandGroupItems}
          dataItemKey="value"
          defaultItem={placeholderOption}
          disabled={filters.surveyId === null}
          style={{ width: '200px' }}
          textField="label"
          value={
            brandGroupItems.find((i) => i.value === filters.brandGroupId) ??
            null
          }
          onChange={(e) => {
            const brandGroupId = (e.value as FilterOption)?.value ?? null
            onFiltersChange({
              ...filters,
              brandGroupId,
              brandId: null,
            })
            if (brandGroupId) {
              logEvent('Select brand group')
            }
          }}
        />
      </div>

      <div style={fieldRowStyle}>
        {t('document_qa_filter_brand_label')}
        <DropDownList
          data={brandItems}
          dataItemKey="value"
          defaultItem={placeholderOption}
          disabled={filters.brandGroupId === null}
          style={{ width: '200px' }}
          textField="label"
          value={brandItems.find((i) => i.value === filters.brandId) ?? null}
          onChange={(e) => {
            const brandId = (e.value as FilterOption)?.value ?? null
            onFiltersChange({
              ...filters,
              brandId,
            })
            if (brandId) {
              logEvent('Select brand')
            }
          }}
        />
      </div>
    </div>
  )
}
